// -------------------------------
// TASK INTERFACES
// -------------------------------
export interface PendingTask {
    name?: string;
    fromDate?: string;
    toDate?: string;
    repeat?: string;
    snoozeTime?: string;
    mode?: string;
    trainertype?: string;
    meetingSchedule?: string;
    attendees?: any[];
    allDay?: boolean;
    description?: string;
}


export interface PendingDeleteTask {
    awaitingName?: boolean;
}

/** 
 * Start creating a calendar task
 */
export const startCreateTaskFlow = (
    setPendingTask: (data: PendingTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {

    if (nameFromIntent) {
        setPendingTask({ name: nameFromIntent });

        pushAIChat(`Got it! When should "${nameFromIntent}" start?`);
        speak(`When should ${nameFromIntent} start?`);
        return;
    }

    setPendingTask({});
    pushAIChat("Sure! What should the task be called?");
    speak("What should the task be called?");
};

export const processPendingTask = (
    text: string,
    pendingTask: PendingTask,
    setPendingTask: (data: PendingTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // 1️⃣ NAME
    if (!pendingTask.name) {
        const cleanName = text.trim();
        if (!cleanName) return pushAIChat("Please provide a valid task name.");

        setPendingTask({ ...pendingTask, name: cleanName });
        pushAIChat(`When should "${cleanName}" start?`);
        speak(`When should ${cleanName} start?`);
        return;
    }

    // 2️⃣ START DATE
    if (!pendingTask.fromDate) {
        setPendingTask({ ...pendingTask, fromDate: text });
        pushAIChat("When should it end?");
        speak("When should it end?");
        return;
    }

    // 3️⃣ END DATE
    if (!pendingTask.toDate) {
        setPendingTask({ ...pendingTask, toDate: text });
        pushAIChat("Should this task repeat? Daily, Weekly, Monthly, or No?");
        speak("Should this task repeat?");
        return;
    }

    // 4️⃣ REPEAT
    if (!pendingTask.repeat) {
        const repeat =
            text.toLowerCase().includes("daily") ? "Daily" :
                text.toLowerCase().includes("weekly") ? "Weekly" :
                    text.toLowerCase().includes("monthly") ? "Monthly" :
                        "Does Not Repeat";

        setPendingTask({ ...pendingTask, repeat });
        pushAIChat("What should be the snooze time? (None, 5 minutes, 10 minutes, 15 minutes)");
        speak("What should be the snooze time?");
        return;
    }

    // 5️⃣ SNOOZE TIME
    if (!pendingTask.snoozeTime) {
        setPendingTask({ ...pendingTask, snoozeTime: text || "None" });
        pushAIChat("Is this event Online or Offline?");
        speak("Is this event online or offline?");
        return;
    }

    // 6️⃣ MODE
    if (!pendingTask.mode) {
        const mode = text.toLowerCase().includes("online") ? "online" : "offline";
        setPendingTask({ ...pendingTask, mode });
        pushAIChat("Is this Internal or External event?");
        speak("Is this internal or external event?");
        return;
    }

    // 7️⃣ EVENT TYPE
    if (!pendingTask.trainertype) {
        const trainertype = text.toLowerCase().includes("external") ? "external" : "internal";
        setPendingTask({ ...pendingTask, trainertype });
        pushAIChat("Please provide Location or Meeting URL.");
        speak("Please provide the location or meeting URL.");
        return;
    }

    // 8️⃣ LOCATION / URL
    if (!pendingTask.meetingSchedule) {
        setPendingTask({ ...pendingTask, meetingSchedule: text });
        pushAIChat("Who are the attendees? (say names or say 'none')");
        speak("Who are the attendees?");
        return;
    }

    // 9️⃣ ATTENDEES
    if (!pendingTask.attendees) {
        const attendees =
            text.toLowerCase().includes("none") ? [] : [];

        setPendingTask({ ...pendingTask, attendees });
        pushAIChat("Is this an all-day event? Yes or No?");
        speak("Is this an all day event?");
        return;
    }

    // 🔟 ALL DAY
    if (pendingTask.allDay === undefined) {
        const allDay = text.toLowerCase().includes("yes");
        setPendingTask({ ...pendingTask, allDay });
        pushAIChat("Any description for this task?");
        speak("Do you want to add a description?");
        return;
    }

    // 1️⃣1️⃣ DESCRIPTION
    if (!pendingTask.description) {
        setPendingTask({ ...pendingTask, description: text || "" });

        pushAIChat(`Creating task "${pendingTask.name}"...`);
        speak(`Creating task ${pendingTask.name}`);

        // 🚀 CREATE TASK
        window.dispatchEvent(
            new CustomEvent("ai-create-task", {
                detail: {
                    name: pendingTask.name,
                    description: pendingTask.description,
                    fromDate: pendingTask.fromDate,
                    toDate: pendingTask.toDate,
                    repeat: pendingTask.repeat,
                    snoozeTime: pendingTask.snoozeTime,
                    meetingSchedule: pendingTask.meetingSchedule,
                    attendees: [
                        ...pendingTask.attendees.map((a: any) => a.email) || pendingTask.attendees || []
                    ],
                    mode: pendingTask.mode,
                    trainertype: pendingTask.trainertype,
                    allDay: pendingTask.allDay
                }
            })
        );

        setPendingTask(null);
    }
};


export const startDeleteTaskFlow = (
    setPendingDelete: (data: PendingDeleteTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {

    if (nameFromIntent) {
        pushAIChat(`Deleting task "${nameFromIntent}"...`);
        speak(`Deleting task ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-task", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which task would you like to delete?");
    speak("Which task would you like to delete?");
};

export const processPendingDeleteTask = (
    text: string,
    pendingDelete: PendingDeleteTask,
    setPendingDelete: (data: PendingDeleteTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    if (!pendingDelete.awaitingName) return;

    const cleanName = text.trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid task name.");
        speak("Please provide a valid task name.");
        return;
    }

    pushAIChat(`Deleting task "${cleanName}"...`);
    speak(`Deleting task ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-task", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};
